import React, { useState, createElement, Component } from 'react';
import { CodeIcon, PaletteIcon, DownloadIcon, TerminalIcon, Loader2Icon, FileCodeIcon } from 'lucide-react';
import { stripCdnUrls } from '../../config/assets';
function buildCSSFile(): string {
  const date = new Date().toLocaleDateString('en-GB');
  return `/* ============================================
 * Smith & Jengs - Custom Stylesheet
 * Generated: ${date}
 * Usage: Enqueue in WordPress via functions.php
 *   wp_enqueue_style('smith-jengs', get_template_directory_uri() . '/css/smith-jengs.css');
 * ============================================ */

/* ── Google Fonts ──────────────────────────── */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Bebas+Neue&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&display=swap');

/* ── CSS Custom Properties (Design Tokens) ── */
:root {
  /* Brand Colors */
  --sj-color-primary: #1a2744;
  --sj-color-accent: #e91e8c;
  --sj-color-accent-hover: #d0187c;
  --sj-color-secondary: #5bbcd5;
  --sj-color-dark: #2b3545;
  --sj-color-light: #f8f9fa;
  --sj-color-text: #1a2744;
  --sj-color-text-muted: #6b7280;
  --sj-color-border: #e5e7eb;

  /* Typography */
  --sj-font-body: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  --sj-font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
  --sj-font-accent: 'Playfair Display', Georgia, serif;

  /* Spacing */
  --sj-container-max: 80rem; /* 1280px */
  --sj-section-py: 4rem;     /* py-16 */
  --sj-section-py-lg: 6rem;  /* py-24 */
  --sj-page-px: 1.5rem;      /* px-6 */
  --sj-page-px-md: 3rem;     /* px-12 */

  /* Border Radius */
  --sj-radius-sm: 0.125rem;
  --sj-radius-md: 0.375rem;
  --sj-radius-lg: 0.75rem;
  --sj-radius-xl: 1rem;
  --sj-radius-full: 9999px;

  /* Shadows */
  --sj-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --sj-shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
}

/* ── Font Fallback Overrides (CLS Prevention) ── */
@font-face {
  font-family: 'Bebas Neue Fallback';
  src: local('Arial Narrow'), local('Arial');
  size-adjust: 95%;
  ascent-override: 100%;
  descent-override: 22%;
  line-gap-override: 0%;
}
@font-face {
  font-family: 'Playfair Fallback';
  src: local('Georgia'), local('Times New Roman');
  size-adjust: 105%;
  ascent-override: 97%;
  descent-override: 25%;
  line-gap-override: 0%;
}
@font-face {
  font-family: 'Poppins Fallback';
  src: local('Arial'), local('Helvetica');
  size-adjust: 107%;
  ascent-override: 92%;
  descent-override: 24%;
  line-gap-override: 0%;
}

/* ── Base Styles ───────────────────────────── */
.sj-body {
  font-family: var(--sj-font-body);
  color: var(--sj-color-text);
  margin: 0;
  background: #fff;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ── Typography ────────────────────────────── */
.sj-heading-display {
  font-family: var(--sj-font-display);
  letter-spacing: 0.05em;
  line-height: 0.85;
  text-transform: uppercase;
  color: var(--sj-color-primary);
}

.sj-heading-accent {
  font-family: var(--sj-font-accent);
  font-style: italic;
  color: var(--sj-color-accent);
}

.sj-text-body {
  font-family: var(--sj-font-body);
  font-size: 1rem;
  line-height: 1.625;
  color: var(--sj-color-text);
}

.sj-text-muted {
  color: var(--sj-color-text-muted);
  font-size: 0.875rem;
}

.sj-text-small {
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 500;
}

/* ── Layout ────────────────────────────────── */
.sj-container {
  max-width: var(--sj-container-max);
  margin-left: auto;
  margin-right: auto;
  padding-left: var(--sj-page-px);
  padding-right: var(--sj-page-px);
}
@media (min-width: 768px) {
  .sj-container {
    padding-left: var(--sj-page-px-md);
    padding-right: var(--sj-page-px-md);
  }
}

.sj-section {
  width: 100%;
  padding-top: var(--sj-section-py);
  padding-bottom: var(--sj-section-py);
}
@media (min-width: 768px) {
  .sj-section {
    padding-top: var(--sj-section-py-lg);
    padding-bottom: var(--sj-section-py-lg);
  }
}

/* ── Buttons ───────────────────────────────── */
.sj-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background-color: var(--sj-color-accent);
  color: #fff;
  padding: 0.75rem 2rem;
  border-radius: var(--sj-radius-full);
  font-weight: 600;
  letter-spacing: 0.025em;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  box-shadow: var(--sj-shadow-md);
}
.sj-btn-primary:hover {
  background-color: var(--sj-color-accent-hover);
}

.sj-btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border-radius: var(--sj-radius-full);
  border: 1px solid var(--sj-color-accent);
  color: var(--sj-color-accent);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-decoration: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}
.sj-btn-outline:hover {
  background-color: var(--sj-color-accent);
  color: #fff;
}

/* ── Navbar ────────────────────────────────── */
.sj-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 3rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  box-shadow: var(--sj-shadow-sm);
}

.sj-navbar-logo {
  width: 6rem;
  height: 6rem;
  object-fit: contain;
}

.sj-nav-link {
  color: var(--sj-color-primary);
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.2s ease;
}
.sj-nav-link:hover {
  color: var(--sj-color-accent);
}

/* ── Footer ────────────────────────────────── */
.sj-footer {
  background-color: var(--sj-color-dark);
  color: #fff;
  padding: 4rem 3rem 2rem;
}

.sj-footer a {
  color: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
}
.sj-footer a:hover {
  color: var(--sj-color-secondary);
}

/* ── Cards & Service Items ─────────────────── */
.sj-service-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  border-radius: var(--sj-radius-xl);
  border: 1px solid var(--sj-color-border);
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--sj-color-primary);
  text-decoration: none;
  transition: all 0.2s ease;
}
.sj-service-card:hover {
  border-color: var(--sj-color-accent);
  background-color: rgba(233, 30, 140, 0.05);
  color: var(--sj-color-accent);
}

/* ── FAQ Accordion ─────────────────────────── */
.sj-faq-item {
  border-bottom: 1px solid var(--sj-color-border);
}

.sj-faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1.25rem 0;
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--sj-font-body);
  font-size: 1rem;
  font-weight: 600;
  color: var(--sj-color-primary);
  text-align: left;
}

.sj-faq-answer {
  padding-bottom: 1.25rem;
  font-size: 0.875rem;
  color: var(--sj-color-text-muted);
  line-height: 1.625;
}

/* ── Cookie Consent Banner ─────────────────── */
.sj-cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9998;
  background: var(--sj-color-dark);
  color: #fff;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.875rem;
}

/* ── Utility: Responsive Grid ──────────────── */
.sj-grid-2col {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  align-items: center;
}
@media (min-width: 1024px) {
  .sj-grid-2col {
    grid-template-columns: 1fr 1fr;
    gap: 6rem;
  }
}
`;
}
function buildJSFile(): string {
  const date = new Date().toLocaleDateString('en-GB');
  return `// ============================================
// Smith & Jengs - WordPress Integration Scripts
// Generated: ${date}
// Usage: Enqueue in WordPress via functions.php
//   wp_enqueue_script('smith-jengs', get_template_directory_uri() . '/js/smith-jengs.js', [], null, true);
// ============================================

(function() {
  'use strict';

  // ── Wait for DOM ────────────────────────────
  document.addEventListener('DOMContentLoaded', function() {
    initImageSlider();
    initFAQAccordion();
    initMobileMenu();
    initCookieConsent();
    initSmoothScroll();
    initScrollAnimations();
  });

  // ── 1. Image Slider / Carousel ──────────────
  // Auto-advancing image slider (used in Hero Banner & Book Online sections)
  // HTML: <div class="sj-slider" data-interval="5000">
  //         <img class="sj-slider-slide" src="..." alt="...">
  //         <img class="sj-slider-slide" src="..." alt="...">
  //       </div>
  function initImageSlider() {
    var sliders = document.querySelectorAll('.sj-slider');
    sliders.forEach(function(slider) {
      var slides = slider.querySelectorAll('.sj-slider-slide');
      if (slides.length < 2) return;

      var current = 0;
      var interval = parseInt(slider.getAttribute('data-interval')) || 5000;

      // Set initial state
      slides.forEach(function(slide, i) {
        slide.style.position = 'absolute';
        slide.style.inset = '0';
        slide.style.width = '100%';
        slide.style.height = '100%';
        slide.style.objectFit = 'cover';
        slide.style.transition = 'opacity 0.6s ease';
        slide.style.opacity = i === 0 ? '1' : '0';
      });
      slider.style.position = 'relative';
      slider.style.overflow = 'hidden';

      // Create dot indicators
      var dotsContainer = document.createElement('div');
      dotsContainer.style.cssText = 'position:absolute;bottom:1.5rem;right:1.5rem;display:flex;gap:0.5rem;z-index:10';
      slides.forEach(function(_, i) {
        var dot = document.createElement('button');
        dot.style.cssText = 'width:0.625rem;height:0.625rem;border-radius:50%;border:none;cursor:pointer;transition:all 0.3s';
        dot.style.background = i === 0 ? '#fff' : 'rgba(255,255,255,0.5)';
        dot.setAttribute('aria-label', 'View image ' + (i + 1));
        dot.addEventListener('click', function() { goToSlide(i); });
        dotsContainer.appendChild(dot);
      });
      slider.appendChild(dotsContainer);

      function goToSlide(index) {
        slides[current].style.opacity = '0';
        current = index;
        slides[current].style.opacity = '1';
        // Update dots
        var dots = dotsContainer.querySelectorAll('button');
        dots.forEach(function(d, i) {
          d.style.background = i === current ? '#fff' : 'rgba(255,255,255,0.5)';
        });
      }

      setInterval(function() {
        goToSlide((current + 1) % slides.length);
      }, interval);
    });
  }

  // ── 2. FAQ Accordion ────────────────────────
  // Expandable FAQ items
  // HTML: <div class="sj-faq-item">
  //         <button class="sj-faq-question">Question?</button>
  //         <div class="sj-faq-answer" hidden>Answer text.</div>
  //       </div>
  function initFAQAccordion() {
    var questions = document.querySelectorAll('.sj-faq-question');
    questions.forEach(function(btn) {
      btn.addEventListener('click', function() {
        var answer = btn.nextElementSibling;
        var isOpen = !answer.hidden;

        // Close all others (accordion behavior)
        questions.forEach(function(otherBtn) {
          var otherAnswer = otherBtn.nextElementSibling;
          if (otherAnswer && otherBtn !== btn) {
            otherAnswer.hidden = true;
            otherBtn.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current
        answer.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  // ── 3. Mobile Menu Toggle ───────────────────
  // Hamburger menu for mobile navigation
  // HTML: <button class="sj-menu-toggle" aria-controls="sj-mobile-menu">
  //         <span></span><span></span><span></span>
  //       </button>
  //       <nav id="sj-mobile-menu" class="sj-mobile-menu" hidden>...</nav>
  function initMobileMenu() {
    var toggle = document.querySelector('.sj-menu-toggle');
    var menu = document.getElementById('sj-mobile-menu');
    if (!toggle || !menu) return;

    toggle.addEventListener('click', function() {
      var isOpen = !menu.hidden;
      menu.hidden = isOpen;
      toggle.setAttribute('aria-expanded', String(!isOpen));
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });

    // Close on link click
    menu.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', function() {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        document.body.style.overflow = '';
      });
    });
  }

  // ── 4. Cookie Consent ───────────────────────
  // GDPR-compliant cookie banner
  // HTML: <div class="sj-cookie-banner" id="sj-cookie-banner">
  //         <p>We use cookies...</p>
  //         <button class="sj-cookie-accept">Accept</button>
  //       </div>
  function initCookieConsent() {
    var banner = document.getElementById('sj-cookie-banner');
    if (!banner) return;

    // Check if already accepted
    if (localStorage.getItem('sj-cookies-accepted') === 'true') {
      banner.style.display = 'none';
      return;
    }

    banner.style.display = '';
    var acceptBtn = banner.querySelector('.sj-cookie-accept');
    if (acceptBtn) {
      acceptBtn.addEventListener('click', function() {
        localStorage.setItem('sj-cookies-accepted', 'true');
        banner.style.display = 'none';
      });
    }
  }

  // ── 5. Smooth Scroll ────────────────────────
  // Smooth scroll for anchor links (e.g. #book-online)
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        var targetId = link.getAttribute('href');
        if (!targetId || targetId === '#') return;
        var target = document.querySelector(targetId);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // ── 6. Scroll Animations (Fade In) ─────────
  // Lightweight IntersectionObserver-based fade-in
  // Add class "sj-animate" to any element you want to fade in on scroll
  // CSS needed: .sj-animate { opacity: 0; transform: translateY(20px); transition: all 0.6s ease; }
  //             .sj-animate.sj-visible { opacity: 1; transform: translateY(0); }
  function initScrollAnimations() {
    var elements = document.querySelectorAll('.sj-animate');
    if (!elements.length || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('sj-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '-50px' });

    elements.forEach(function(el) { observer.observe(el); });
  }

  // ── 7. Structured Data Helper ───────────────
  // Inject JSON-LD structured data for SEO
  // Call: sjInjectStructuredData({ "@type": "LocalBusiness", ... })
  window.sjInjectStructuredData = function(data) {
    var script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(Object.assign({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Smith & Jengs Cleaning Services",
      "url": "https://smithandjengs.com",
      "logo": "/assets/sj-website-logo-colour.svg",
      "telephone": ["07432285564", "01869601439"],
      "email": "smithsandjengs@yahoo.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bicester",
        "addressRegion": "Oxfordshire",
        "addressCountry": "GB"
      }
    }, data));
    document.head.appendChild(script);
  };

})();
`;
}
function buildComponentManifest(): string {
  const date = new Date().toLocaleDateString('en-GB');
  const lines = ['// ============================================', '// Smith & Jengs - Component Library Manifest', '// Generated: ' + date, '// Framework: React + TypeScript + Tailwind CSS', '// ============================================', '', '// SHARED COMPONENTS', '// -----------------', "export { Navbar } from './components/Navbar'           // Sticky header with mobile menu, service dropdown", "export { Footer } from './components/Footer'           // Full footer with sitemap, services, contact info", "export { ServiceNav } from './components/ServiceNav'   // Horizontal service tab navigation bar", "export { HeroBanner } from './components/HeroBanner'   // Homepage hero with 5-image slideshow", "export { BookOnline } from './components/BookOnline'    // 3-step booking section with image slider", "export { OurServices } from './components/OurServices'  // Service listing with background image", "export { HousekeepingRevolution } from './components/HousekeepingRevolution' // Split layout feature section", "export { FAQ } from './components/FAQ'                  // Accordion FAQ with side image", "export { WhyChooseUs } from './components/WhyChooseUs'  // Large typography + image + info card", "export { SEOHead } from './components/SEOHead'          // Dynamic meta tags and preloads", "export { CookieConsent } from './components/CookieConsent' // GDPR cookie banner", '', '// PAGE COMPONENTS', '// ---------------', "export { CommercialCleaning } from './pages/CommercialCleaning'", "export { BasicHomeCleaning } from './pages/BasicHomeCleaning'", "export { SpringDeepCleaning } from './pages/SpringDeepCleaning'", "export { AboutUs } from './pages/AboutUs'", "export { ContactUs } from './pages/ContactUs'", "export { FAQPage } from './pages/FAQPage'", "export { PrivacyPolicy } from './pages/PrivacyPolicy'", '', '// QUOTE PAGES', '// -----------', "export { CommercialQuote } from './pages/CommercialQuote'", "export { BasicHomeCleaningQuote } from './pages/BasicHomeCleaningQuote'", "export { SpringDeepCleaningQuote } from './pages/SpringDeepCleaningQuote'", "export { MoveInMoveOutQuote } from './pages/MoveInMoveOutQuote'", "export { AirbnbQuote } from './pages/AirbnbQuote'", "export { PostRenovationQuote } from './pages/PostRenovationQuote'", '', '// ASSET CONFIG', '// ------------', "export { ASSETS, resolveAsset, cleanAssetPath } from './config/assets'", '', '// DESIGN TOKENS', '// -------------', '// Colors:  #1a2744 (navy), #e91e8c (pink), #5bbcd5 (teal), #2b3545 (dark), #f8f9fa (light)', '// Fonts:   Poppins (body), Bebas Neue (display), Playfair Display (accent)', '// Spacing: max-w-7xl container, py-16/py-24 sections, px-6/px-12 page padding'];
  return lines.join('\n');
}
export function CodeExportsTab() {
  const [exporting, setExporting] = useState<string | null>(null);
  const exportOptions = [{
    id: 'css',
    title: 'Stylesheet (CSS)',
    description: 'Design tokens, typography, layout & component styles',
    icon: <PaletteIcon className="w-5 h-5 text-blue-400" data-id="element-122" />,
    format: 'CSS'
  }, {
    id: 'js',
    title: 'Scripts (JS)',
    description: 'Slider, accordion, menu, cookies & scroll animations',
    icon: <FileCodeIcon className="w-5 h-5 text-yellow-400" data-id="element-123" />,
    format: 'JS'
  }, {
    id: 'components',
    title: 'Component Library',
    description: 'React components with Tailwind classes',
    icon: <CodeIcon className="w-5 h-5 text-pink-400" data-id="element-124" />,
    format: 'TSX'
  }];
  const handleExport = async (id: string) => {
    setExporting(id);
    await new Promise(r => setTimeout(r, 500));
    let rawContent: string;
    let mimeType: string;
    let ext: string;
    switch (id) {
      case 'css':
        rawContent = buildCSSFile();
        mimeType = 'text/css';
        ext = 'css';
        break;
      case 'js':
        rawContent = buildJSFile();
        mimeType = 'text/javascript';
        ext = 'js';
        break;
      default:
        rawContent = buildComponentManifest();
        mimeType = 'text/plain';
        ext = 'tsx';
        break;
    }
    // Strip any CDN references from exported files
    const content = stripCdnUrls(rawContent);
    const blob = new Blob([content], {
      type: mimeType
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'smith-jengs-' + id + '.' + ext;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setExporting(null);
  };
  return <div className="p-4 space-y-6" data-id="element-125">
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-md p-4 flex items-start gap-3" data-id="element-126">
        <TerminalIcon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" data-id="element-127" />
        <div data-id="element-128">
          <h3 className="text-sm font-medium text-blue-100 mb-1" data-id="element-129">
            WordPress Ready
          </h3>
          <p className="text-xs text-blue-200/70 leading-relaxed" data-id="element-130">
            Download CSS and JS files ready to enqueue in your WordPress theme
            via{' '}
            <code className="bg-slate-800 border border-slate-700 rounded px-1 py-0.5 font-mono text-[10px] text-slate-300" data-id="element-131">
              functions.php
            </code>
            . Each file includes usage instructions at the top.
          </p>
        </div>
      </div>

      <div className="space-y-3" data-id="element-132">
        <h3 className="text-sm font-semibold text-white mb-2" data-id="element-133">
          Project Exports
        </h3>
        {exportOptions.map(option => <button key={option.id} onClick={() => handleExport(option.id)} disabled={exporting === option.id} className="w-full group flex items-center justify-between p-4 rounded-md bg-slate-800 border border-slate-700 hover:border-blue-500 hover:bg-slate-800/80 transition-all text-left disabled:opacity-70" data-id="element-134">
            <div className="flex items-center gap-4" data-id="element-135">
              <div className="p-2 bg-slate-900 rounded-md group-hover:scale-110 transition-transform" data-id="element-136">
                {option.icon}
              </div>
              <div data-id="element-137">
                <div className="text-sm font-medium text-slate-200 group-hover:text-white transition-colors" data-id="element-138">
                  {option.title}
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-id="element-139">
                  {option.description}
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2" data-id="element-140">
              <span className="text-[10px] font-mono font-medium text-slate-500 bg-slate-900 px-2 py-1 rounded" data-id="element-141">
                {option.format}
              </span>
              {exporting === option.id ? <Loader2Icon className="w-4 h-4 text-blue-400 animate-spin" data-id="element-142" /> : <DownloadIcon className="w-4 h-4 text-slate-600 group-hover:text-blue-400 transition-colors" data-id="element-143" />}
            </div>
          </button>)}
      </div>
    </div>;
}